import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useDateRange } from "../../context/DateRangeContext";
import api from "../../utils/api";
import {
  IconLayoutDashboard,
  IconSettings2,
  IconCalculator,
  IconTag,
  IconPackages,
  IconShoppingCart,
  IconUsers,
  IconTruck,
  IconChartBar,
  IconBuildingStore,
  IconAdjustments,
  IconShield,
  IconDroplet,
  IconBell,
  IconLogout,
  IconMenu2,
  IconShoppingBag,
  IconBottle,
  IconCash,
  IconBox,
  IconBuildingFactory,
  IconSettings,
  IconLock,
  IconX,
  IconCalendar,
  IconClipboardList,
  IconReceipt,
} from "@tabler/icons-react";

const NAV = [
  {
    section: "Overview",
    items: [
      {
        to: "/dashboard",
        icon: IconLayoutDashboard,
        label: "Dashboard",
        module: "dashboard",
      },
    ],
  },
  {
    section: "Operations",
    items: [
      {
        to: "/production",
        icon: IconSettings2,
        label: "Production",
        module: "production",
      },
      {
        to: "/costs",
        icon: IconCalculator,
        label: "Cost Lines",
        module: "costs",
      },
      {
        to: "/pricing",
        icon: IconTag,
        label: "Pricing",
        module: "pricing",
      },
      {
        to: "/products",
        icon: IconBox,
        label: "Products",
        module: "products",
      },
      {
        to: "/inventory",
        icon: IconPackages,
        label: "Inventory",
        module: "inventory",
      },
      {
        to: "/procurement",
        icon: IconShoppingBag,
        label: "Procurement",
        module: "procurement",
      },
      {
        to: "/suppliers",
        icon: IconBuildingFactory,
        label: "Suppliers",
        module: "suppliers",
      },
    ],
  },
  {
    section: "Sales & Delivery",
    items: [
      {
        to: "/sales",
        icon: IconReceipt,
        label: "Sales",
        module: "sales",
      },
      {
        to: "/orders",
        icon: IconShoppingCart,
        label: "Orders",
        module: "orders",
      },
      {
        to: "/customers",
        icon: IconUsers,
        label: "Customers",
        module: "customers",
      },
      {
        to: "/delivery",
        icon: IconTruck,
        label: "Delivery",
        module: "delivery",
      },
      {
        to: "/bottles",
        icon: IconBottle,
        label: "Bottle Deposits",
        module: "bottles",
      },
    ],
  },
  {
    section: "Finance",
    items: [
      {
        to: "/expenses",
        icon: IconCash,
        label: "Expenses",
        module: "expenses",
      },
    ],
  },
  {
    section: "Analytics",
    items: [
      {
        to: "/reports",
        icon: IconChartBar,
        label: "Reports",
        module: "reports",
      },
    ],
  },
  {
    section: "Administration",
    superOnly: true,
    items: [
      {
        to: "/branches",
        icon: IconBuildingStore,
        label: "Branches",
        module: "branches",
      },
      {
        to: "/roles",
        icon: IconAdjustments,
        label: "Roles",
        module: "roles",
        superOnly: true,
      },
      {
        to: "/users",
        icon: IconShield,
        label: "Users & Permissions",
        module: "users",
      },
      {
        to: "/settings",
        icon: IconSettings,
        label: "Company Settings",
        module: "settings",
        superOnly: true,
      },
      {
        to: "/activity-logs",
        icon: IconClipboardList,
        label: "Activity Logs",
        superOnly: true,
      },
    ],
  },
];

const isDriverUser = (user) =>
  String(user?.role_name || "")
    .trim()
    .toLowerCase() === "driver";

// Derives a 1–2 letter avatar from the user's name
function getInitials(name = "") {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Returns current month + year e.g. "June 2026"
function currentMonthYear() {
  return new Date().toLocaleString("en-KE", { month: "long", year: "numeric" });
}

// Derives a page label from the current pathname
function getPageLabel(pathname) {
  const map = {
    "/dashboard": "Dashboard",
    "/production": "Production",
    "/costs": "Cost Lines",
    "/pricing": "Pricing",
    "/inventory": "Inventory",
    "/procurement": "Procurement",
    "/orders": "Orders",
    "/customers": "Customers",
    "/delivery": "Delivery",
    "/bottles": "Bottle Deposits",
    "/expenses": "Expenses",
    "/reports": "Reports",
    "/products": "Products",
    "/suppliers": "Suppliers",
    "/branches": "Branches",
    "/roles": "Roles",
    "/users": "Users & Permissions",
    "/notifications": "Notifications",
    "/settings": "Company Settings",
    "/activity-logs": "Activity Logs",
    "/sales": "Sales",
  };
  return map[pathname] || "Dashboard";
}

const MOBILE_BREAKPOINT = 768;

// Dark navy sidebar colours
const SIDEBAR_BG = "#0f2044";
const SIDEBAR_ACTIVE_BG = "rgba(255,255,255,0.12)";
const SIDEBAR_HOVER_BG = "rgba(255,255,255,0.07)";
const SIDEBAR_TEXT = "rgba(255,255,255,0.75)";
const SIDEBAR_TEXT_ACTIVE = "#ffffff";
const SIDEBAR_SECTION = "rgba(255,255,255,0.35)";
const SIDEBAR_BORDER = "rgba(255,255,255,0.1)";

const PERIODS = ['Today', 'Week', 'Month', 'Year', 'Custom Date'];

function DateSelector({ isMobile }) {
  const { period, changePeriod, offset, goBack, goForward, goNow, label, nowLabel,
    customFrom, setCustomFrom, customTo, setCustomTo } = useDateRange();

  const btnBase = {
    border: 'none', cursor: 'pointer', borderRadius: 7,
    fontSize: 13, fontWeight: 500, padding: '6px 14px', transition: 'all 0.15s',
  };

  const arrowBtn = {
    ...btnBase,
    background: '#f1f5f9', color: '#475569',
    width: 32, height: 32, padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 15,
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        padding: isMobile ? '8px 0' : '10px 0',
      }}>
        {/* Period tabs */}
        <div style={{ display: 'flex', background: '#f8fafc', borderRadius: 9, padding: 3, border: '1px solid #e2e8f0', gap: 2 }}>
          {PERIODS.map(p => {
            const key = p === 'Custom Date' ? 'custom' : p.toLowerCase();
            const active = period === key;
            return (
              <button key={p} onClick={() => changePeriod(key)} style={{
                ...btnBase,
                background: active ? '#1565c0' : 'transparent',
                color: active ? '#fff' : '#64748b',
                fontWeight: active ? 700 : 500,
                padding: isMobile ? '5px 10px' : '6px 14px',
                fontSize: isMobile ? 12 : 13,
              }}>
                {p}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={goBack} style={arrowBtn}>‹</button>
          <span style={{
            fontSize: 14, fontWeight: 600, color: '#0f172a',
            minWidth: isMobile ? 90 : 130, textAlign: 'center',
          }}>
            {label}
          </span>
          <button onClick={goForward} disabled={offset >= 0} style={{
            ...arrowBtn,
            opacity: offset >= 0 ? 0.35 : 1,
            cursor: offset >= 0 ? 'not-allowed' : 'pointer',
          }}>›</button>
        </div>

        {/* This Month / Week / Year */}
        <button onClick={goNow} style={{
          ...btnBase,
          background: offset === 0 && period !== 'custom' ? '#1565c0' : '#eff6ff',
          color: offset === 0 && period !== 'custom' ? '#fff' : '#1565c0',
          fontWeight: 700,
          border: '1px solid #bfdbfe',
        }}>
          {nowLabel}
        </button>
      </div>

      {/* Custom date inputs */}
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 10 }}>
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>From</label>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
            style={{ fontSize: 13, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 7 }} />
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>To</label>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
            style={{ fontSize: 13, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 7 }} />
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const {
    user,
    isSuper,
    can,
    logout,
    activeBranchId,
    activeBranchName,
    selectBranch,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDriver = isDriverUser(user);

  const [notifCount, setNotifCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifCount(data.filter((n) => !n.is_read).length);
      setNotifs(data);
    } catch { }
  }, []);

  useEffect(() => {
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, [loadNotifs]);

  const markNotifRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    loadNotifs();
  };

  const markAllRead = async () => {
    await api.patch("/notifications/read-all");
    loadNotifs();
  };

  const changePassword = async () => {
    if (pwForm.new.length < 8) { setPwMsg("Password must be at least 8 characters"); return; }
    if (pwForm.new !== pwForm.confirm) { setPwMsg("Passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.new,
      });
      setPwMsg("✓ Password updated!");
      setTimeout(() => {
        setPwModal(false);
        setPwForm({ current: "", new: "", confirm: "" });
        setPwMsg("");
      }, 1500);
    } catch (err) {
      setPwMsg(err.response?.data?.message || "Error updating password");
    }
  };

  useEffect(() => {
    if (!isSuper) {
      setBranches([]);
      return;
    }
    let cancelled = false;
    const loadBranches = async () => {
      setBranchLoading(true);
      try {
        const { data } = await api.get("/branches");
        const branchRows = data || [];
        if (cancelled) return;
        setBranches(branchRows);
        if (
          activeBranchId &&
          !branchRows.some(
            (branch) => String(branch.id) === String(activeBranchId),
          )
        ) {
          selectBranch(null);
        }
      } catch {
        if (!cancelled) setBranches([]);
      } finally {
        if (!cancelled) setBranchLoading(false);
      }
    };
    loadBranches();
    return () => { cancelled = true; };
  }, [isSuper, activeBranchId, selectBranch]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeSidebarOnMobile = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const sidebarStyle = {
    width: 230,
    flexShrink: 0,
    background: SIDEBAR_BG,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    height: "100vh",
    zIndex: 300,
    transition: "transform 0.25s ease",
    ...(isMobile
      ? {
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        boxShadow: sidebarOpen ? "0 20px 40px rgba(0,0,0,0.4)" : "none",
      }
      : {
        position: "relative",
        transform: "none",
      }),
  };

  const pageLabel = getPageLabel(location.pathname);
  const monthYear = currentMonthYear();
  const initials = getInitials(user?.name);

  const userContextLabel = isSuper
    ? "Super Admin · All branches"
    : isDriver
      ? `Driver · ${user?.branch_name || "Assigned branch"}`
      : user?.branch_name || user?.role_name || "User";

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
        background: "#f1f5f9",
      }}
    >
      {/* ── Change Password Modal ── */}
      {pwModal && (
        <div
          onClick={() => setPwModal(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
            zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, padding: 24,
              width: 360, border: "1px solid #e2e8f0",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: "#0f172a" }}>
              Change password
            </div>
            {[["Current password", "current"], ["New password", "new"], ["Confirm new password", "confirm"]].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginBottom: 4 }}>{label}</div>
                <input
                  type="password"
                  value={pwForm[key]}
                  onChange={(e) => setPwForm((f) => ({ ...f, [key]: e.target.value }))}
                  style={{
                    width: "100%", fontSize: 13, padding: "8px 11px",
                    border: "1px solid #e2e8f0", borderRadius: 8,
                    outline: "none", boxSizing: "border-box", color: "#0f172a",
                  }}
                />
              </div>
            ))}
            {pwMsg && (
              <div style={{ fontSize: 12, color: pwMsg.startsWith("✓") ? "#16a34a" : "#dc2626", marginBottom: 10 }}>
                {pwMsg}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                onClick={changePassword}
                style={{
                  padding: "8px 16px", background: "#1565c0", color: "#fff",
                  border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                Update password
              </button>
              <button
                onClick={() => { setPwModal(false); setPwMsg(""); }}
                style={{
                  padding: "8px 16px", border: "1px solid #e2e8f0",
                  borderRadius: 8, fontSize: 13, cursor: "pointer",
                  background: "#fff", color: "#64748b",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile overlay ── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 250,
          }}
        />
      )}

      {/* ── Sidebar ── */}
      <aside style={sidebarStyle}>

        {/* Logo + close button */}
        <div style={{ padding: "18px 16px 14px", borderBottom: `1px solid ${SIDEBAR_BORDER}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34, height: 34,
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: 9,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconDroplet size={19} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                  Jabari Purified Water
                </div>
                <div style={{ fontSize: 9, color: SIDEBAR_SECTION, letterSpacing: "0.6px", textTransform: "uppercase", marginTop: 1 }}>
                  Strength in every drop
                </div>
              </div>
            </div>

            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                aria-label="Close menu"
                style={{
                  border: `1px solid ${SIDEBAR_BORDER}`,
                  background: "transparent",
                  borderRadius: 7,
                  width: 30, height: 30,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  color: SIDEBAR_TEXT,
                }}
              >
                <IconX size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, paddingTop: 8, paddingBottom: 8 }}>
          {NAV.map((section) => {
            if (section.superOnly && !isSuper) return null;

            const visibleItems = section.items.filter((item) => {
              if (item.superOnly && !isSuper) return false;
              if (isDriver && item.module === "dashboard") return false;
              return can(item.module);
            });

            if (!visibleItems.length) return null;

            return (
              <div key={section.section}>
                <div
                  style={{
                    padding: "10px 16px 4px",
                    fontSize: 9,
                    fontWeight: 700,
                    color: SIDEBAR_SECTION,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                  }}
                >
                  {section.section}
                </div>

                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeSidebarOnMobile}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 16px",
                      margin: "1px 8px",
                      borderRadius: 8,
                      fontSize: 13,
                      textDecoration: "none",
                      color: isActive ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT,
                      background: isActive ? SIDEBAR_ACTIVE_BG : "transparent",
                      fontWeight: isActive ? 600 : 400,
                      transition: "background 0.15s, color 0.15s",
                    })}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.dataset.active) {
                        e.currentTarget.style.background = SIDEBAR_HOVER_BG;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.currentTarget.dataset.active) {
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  >
                    <item.icon size={16} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content area ── */}
      <div
        style={{
          flex: 1, minWidth: 0,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ── Top bar ── */}
        <div
          style={{
            padding: isMobile ? "10px 14px" : "0 24px",
            height: isMobile ? "auto" : 52,
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            gap: 12,
          }}
        >
          {/* Left: hamburger (mobile) + page title + date */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                style={{
                  border: "none", background: "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#0f172a", padding: 4, flexShrink: 0,
                }}
              >
                <IconMenu2 size={20} />
              </button>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span
                style={{
                  fontSize: isMobile ? 15 : 16,
                  fontWeight: 700,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                }}
              >
                {pageLabel}
              </span>

            </div>
          </div>

          {/* Right: branch selector + bell */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {isSuper ? (
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <select
                  value={activeBranchId || ""}
                  disabled={branchLoading}
                  onChange={(event) => {
                    const selectedId = event.target.value;
                    if (!selectedId) { selectBranch(null); return; }
                    const selectedBranch = branches.find(
                      (branch) => String(branch.id) === String(selectedId),
                    );
                    selectBranch(selectedBranch || null);
                  }}
                  aria-label="Select branch"
                  style={{
                    height: 34,
                    padding: "0 28px 0 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    background: "#f8fafc",
                    color: "#0f172a",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: branchLoading ? "wait" : "pointer",
                    outline: "none",
                    appearance: "none",
                    minWidth: isMobile ? 120 : 160,
                    maxWidth: isMobile ? "36vw" : 200,
                  }}
                >
                  <option value="">
                    {branchLoading ? "Loading..." : "All Branches"}
                  </option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {/* custom chevron */}
                <svg
                  style={{ position: "absolute", right: 8, pointerEvents: "none", color: "#64748b" }}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            ) : (
              <div
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1565c0",
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? 120 : 200,
                }}
              >
                {activeBranchName}
              </div>
            )}

            {/* Bell */}
            <button
              onClick={() => {
                setNotifOpen((v) => !v);
                setProfileOpen(false);
              }}
              style={{
                position: "relative",
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36,
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                background: "#f8fafc",
                cursor: "pointer",
                color: "#0f172a",
                flexShrink: 0,
              }}
            >
              <IconBell size={17} />
              {notifCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -4, right: -4,
                    minWidth: 17, height: 17,
                    padding: "0 4px",
                    background: "#ef4444",
                    color: "#fff",
                    borderRadius: 999,
                    fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {notifCount}
                </span>
              )}
            </button>

            {/* Notification panel */}
            {notifOpen && (
              <div
                onClick={() => setNotifOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 150 }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "fixed", top: 56, right: 16, width: 340,
                    background: "#fff", border: "1px solid #e2e8f0",
                    borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    zIndex: 151, maxHeight: 420, overflow: "hidden",
                    display: "flex", flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px", fontWeight: 600, fontSize: 13,
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      color: "#0f172a",
                    }}
                  >
                    Notifications
                    {notifCount > 0 && (
                      <button
                        onClick={markAllRead}
                        style={{ fontSize: 11, color: "#1565c0", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div style={{ overflowY: "auto", flex: 1 }}>
                    {notifs.length === 0
                      ? (
                        <div style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                          No notifications
                        </div>
                      )
                      : notifs.map((n) => (
                        <div
                          key={n.id}
                          onClick={() => markNotifRead(n.id)}
                          style={{
                            padding: "11px 16px",
                            borderBottom: "1px solid #f1f5f9",
                            background: n.is_read ? "#fff" : "#f0f9ff",
                            cursor: "pointer", fontSize: 12,
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 2, color: "#0f172a" }}>{n.title}</div>
                          <div style={{ color: "#64748b" }}>{n.message}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>
                            {new Date(n.created_at).toLocaleString()}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            )}
            {/* Profile menu */}
            <div
              style={{
                position: "relative",
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => {
                  setProfileOpen((open) => !open);
                  setNotifOpen(false);
                }}
                aria-label="Open profile menu"
                style={{
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: isMobile ? 2 : "3px 10px 3px 4px",
                  border: "1px solid #e2e8f0",
                  borderRadius: 9,
                  background: profileOpen ? "#eff6ff" : "#f8fafc",
                  cursor: "pointer",
                  color: "#0f172a",
                  maxWidth: 230,
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "#1565c0",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </div>

                {/* Name and role – hidden on mobile */}
                {!isMobile && (
                  <>
                    <div
                      style={{
                        minWidth: 0,
                        textAlign: "left",
                        lineHeight: 1.2,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 130,
                        }}
                      >
                        {user?.name}
                      </div>

                      <div
                        style={{
                          fontSize: 9,
                          color: "#64748b",
                          marginTop: 2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 130,
                        }}
                      >
                        {userContextLabel}
                      </div>
                    </div>

                    <span
                      style={{
                        fontSize: 13,
                        color: "#64748b",
                        marginLeft: 2,
                      }}
                    >
                      ▾
                    </span>
                  </>
                )}
              </button>

              {profileOpen && (
                <>
                  {/* Close the dropdown when clicking elsewhere */}
                  <div
                    onClick={() => setProfileOpen(false)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 190,
                    }}
                  />

                  {/* Dropdown */}
                  <div
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: 44,
                      right: 0,
                      width: 260,
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      boxShadow: "0 12px 35px rgba(15, 23, 42, 0.16)",
                      overflow: "hidden",
                      zIndex: 200,
                    }}
                  >
                    {/* Profile details */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        padding: "14px 15px",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: "#1565c0",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {user?.name}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: "#64748b",
                            marginTop: 3,
                          }}
                        >
                          {userContextLabel}
                        </div>
                      </div>
                    </div>

                    {/* Profile actions */}
                    <div style={{ padding: 6 }}>
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          setPwModal(true);
                        }}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 11px",
                          border: "none",
                          borderRadius: 8,
                          background: "transparent",
                          color: "#334155",
                          fontSize: 12,
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = "transparent";
                        }}
                      >
                        <IconLock size={16} />
                        Change Password
                      </button>

                      <button
                        onClick={handleLogout}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 11px",
                          border: "none",
                          borderRadius: 8,
                          background: "transparent",
                          color: "#dc2626",
                          fontSize: 12,
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "#fef2f2";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = "transparent";
                        }}
                      >
                        <IconLogout size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Date selector bar ── */}
        {!isDriver && (
          <div style={{
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            padding: isMobile ? "0 14px" : "0 24px",
            flexShrink: 0,
          }}>
            <DateSelector isMobile={isMobile} />
          </div>
        )}

        {/* ── Page content ── */}
        <main
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: isMobile ? "12px" : "20px 24px",
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}